import { DateTime } from 'luxon'
import * as Sentry from '@sentry/react'

/**
 * Handle parsing of different possible time formats
 */

const knownFormats = [
  // Date only
  'dd.MM.yy',
  'dd.MM.yyyy',
  'dd-MM-yyyy',
  'dd/MM/yyyy',
  'MM/dd/yyyy',
  'yyyy-MM-dd',
  'yyyy/MM/dd',
  'yyyyMMdd',
  'dd MMM yyyy',
  'd MMM yyyy',
  'MMM d, yyyy',

  // Date + Time
  'dd-MM-yyyy HH:mm',
  'dd-MM-yyyy HH:mm:ss',
  'dd/MM/yyyy HH:mm',
  'dd/MM/yyyy HH:mm:ss',
  'yyyy-MM-dd HH:mm',
  'yyyy-MM-dd HH:mm:ss',
  "yyyy-MM-dd'T'HH:mm",
  "yyyy-MM-dd'T'HH:mm:ss"
]

export function parseDateOrUndefined(dateStr: string, options?: { assumeUTC?: boolean; assumeLocal?: boolean }): string | undefined | null {
  if (!dateStr?.trim()) return undefined

  let dt: DateTime | undefined = undefined

  const isUnix = /^\d{10}$/.test(dateStr) || /^\d{13}$/.test(dateStr)
  if (isUnix) {
    const timestamp = parseInt(dateStr, 10)
    dt = DateTime.fromMillis(
      dateStr.length === 10 ? timestamp * 1000 : timestamp,
      { zone: 'utc' } // or 'local', depending on expected default
    )
  }

  // Try parsing from ISO or common human-readable format
  if (!dt || !dt.isValid) {
    dt = DateTime.fromISO(dateStr, { setZone: true })
  }

  // If that fails, try from common format heuristics
  if (!dt.isValid) {
    for (const format of knownFormats) {
      dt = DateTime.fromFormat(dateStr, format, { zone: 'default' })
      if (dt.isValid) break
    }
  }

  // If still invalid, attempt JS Date fallback
  if (!dt.isValid) {
    dt = DateTime.fromJSDate(new Date(dateStr), { zone: 'default' })
  }

  if (!dt.isValid) {
    /* capture failed parse attempt to sentry so we can examine if there are frequently failing formats */
    Sentry.captureMessage('Date parsing failed', {
      level: 'warning',
      tags: { feature: 'csv-date-parser' },
      extra: { input: dateStr }
    })
    return undefined
  }
  /*
   * Whether the cell itself said which zone it was in. `dt.zoneName` cannot answer this: luxon resolves
   * a named zone to the concrete IANA name, so it never reads back as "local" and the assumption below
   * was never applied at all. A unix timestamp is an absolute instant and must not be reinterpreted.
   *
   * Every zone above is luxon's `'default'`, never `'local'` or `Intl`: those two are the *system* zone,
   * which `Settings.defaultZone` cannot override, so the viewer's zone would be read from one source
   * here and from another in `formatDate`. The two agree in a browser and diverge on a UTC build agent.
   */
  const cellCarriesZone = isUnix || /(?:Z|[+-]\d{2}:?\d{2})(?:\[[^\]]+\])?$/i.test(dateStr.trim())

  if (!cellCarriesZone) {
    if (options?.assumeUTC) {
      dt = dt.setZone('utc', { keepLocalTime: true })
    } else if (options?.assumeLocal) {
      dt = dt.setZone('default', { keepLocalTime: true })
    }
  }

  return dt.isValid ? dt.toISO() : undefined
}
